import { useState, useEffect, useRef } from "react";
import { BrowserRouter } from "react-router-dom";
import "./App.css";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { useDispatch } from "react-redux";
import { fetchProfileInfo } from "./redux/profileSlice";
import { baseFetch } from "./helpers/apiClient";
import AppContent from "./AppContent";
import { SnackbarProvider } from "./context/SnackbarContext";
import { isRedirectTokenLink } from "./helpers/redirectTokenLink";

const theme = createTheme({
  typography: {
    fontFamily: `"PT Sans", serif`,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: `"PT Sans", serif`,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        shrink: {
          backgroundColor: "#fff",
          paddingLeft: 4,
          paddingRight: 4,
        },
      },
    },
  },
});

function App() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(localStorage.getItem("userId"));
  const [token, setToken] = useState(localStorage.getItem("token"));

  const queryParams = new URLSearchParams(window.location.search);
  // Only a sign-in-by-link arrival gates the app (ECH-375). Testing for any
  // parameter meant a signed-out visitor with `?signup=1`, or any campaign
  // parameter, hit the failure branch below and was hard-redirected to `/`
  // with the query stripped.
  const isSignInLink = isRedirectTokenLink(queryParams);
  const redirectUserId = queryParams.get("userId");
  const redirectToken = queryParams.get("redirectToken");

  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    const fetchUserData = async () => {
      if (!isSignInLink) {
        setLoading(false);
        return;
      }

      const newUserId = redirectUserId;
      const normalizedRedirectToken = redirectToken?.replace(/ /g, "+");

      if (!newUserId || !normalizedRedirectToken) {
        setLoading(false);
        return;
      }

      try {
        const response = await baseFetch({
          url: "/v1/web/profile/verify/redirectToken",
          method: "POST",
          body: {
            token: normalizedRedirectToken,
            userId: newUserId,
          },
        });
        // Source role and userId from the User doc (`profile`) rather than the
        // Profile doc (`userData`), which can be null or have an empty role for
        // anyone resuming a partial signup. Reading from `userData` was coercing
        // undefined to the string "undefined", which then failed every
        // ProtectedRoute role check and bounced the user back to /login.
        const profileRes = response?.data?.profile;
        const userIdValue =
          profileRes?.id ?? response?.data?.userData?.userId ?? "";
        localStorage.setItem("token", response?.data?.tokens?.access?.token);
        localStorage.setItem(
          "refreshToken",
          response?.data?.tokens?.refresh?.token,
        );
        localStorage.setItem("userId", userIdValue);
        localStorage.setItem("role", profileRes?.role?.join("_") ?? "");
        setUserId(userIdValue);
        setToken(response?.data?.tokens?.access?.token);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [isSignInLink, redirectToken, redirectUserId]);

  useEffect(() => {
    if (userId && token && !loading) {
      dispatch(fetchProfileInfo());
    }
  }, [userId, token, dispatch, loading]);

  // Hold the app only while a sign-in link is being exchanged for a session.
  //
  // The failure branch is a hard navigation that drops the query string, so
  // widening this to "any query parameter" silently discarded every other kind
  // of link — `?signup=1` among them (ECH-375).
  if (isSignInLink) {
    if (loading) {
      return <div>Loading...</div>;
    }

    if (!token || !userId) {
      window.location.replace("/");
      return null;
    }
  }

  // Render the main app if there are no query parameters or if token and userId are available
  return (
    <ThemeProvider theme={theme}>
      <BrowserRouter>
        <SnackbarProvider>
          <AppContent />
        </SnackbarProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
